/**
 * Environment context utilities for Otto Portal.
 *
 * Reads deployment-environment indicators from three sources, in priority order:
 *   1. <meta> tags injected by docker-entrypoint.sh at container start
 *      (ACA env vars: OTTO_ENV / OTTO_REGION / APPINSIGHTS_CONN).
 *      This is the ground-truth identity of the ACA serving the bundle —
 *      independent of any URL the user navigated to within the SPA.
 *   2. URL `?env=` query parameter, set by AFD routing rules when traffic
 *      lands on staging/canary slots (preserved across SPA navigations
 *      by QueryParamPreserver). Used as a fallback when the meta tag
 *      placeholder wasn't substituted (e.g., local docker run without
 *      OTTO_ENV set; local pnpm dev where there's no entrypoint at all).
 *   3. undefined — the portal is running in production by default.
 *
 * The meta-tag path is preferred because it survives any future routing
 * changes and isn't spoofable via URL. The URL fallback is kept so the
 * existing staging-slot detection keeps working unchanged when the
 * companion ACA env vars haven't been set yet.
 */

/** Valid Otto environment values. */
export type OttoEnv = 'staging' | 'prod' | 'canary';

const VALID_ENVS: ReadonlySet<string> = new Set<string>(['staging', 'prod', 'canary']);

/**
 * Read a <meta name="..."> content value from the current document.
 *
 * Returns undefined when:
 *  - the tag is missing entirely
 *  - the content is empty
 *  - the content still contains an unsubstituted __PLACEHOLDER__ template
 *    (i.e., docker-entrypoint.sh didn't have the matching env var)
 *
 * SSR-safe: returns undefined when `document` is not defined.
 */
function readMetaTag(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const el = document.querySelector(`meta[name="${name}"]`);
  const content = el?.getAttribute('content')?.trim();
  if (!content) return undefined;
  // Unsubstituted entrypoint placeholders look like __OTTO_ENV__ — reject.
  if (content.startsWith('__') && content.endsWith('__')) return undefined;
  return content;
}

/**
 * Returns the current Otto deployment environment.
 *
 * Reads <meta name="otto-env"> first (ACA-injected ground truth), then
 * falls back to the URL `?env=` param. Returns undefined when neither
 * source has a recognized value — callers should treat that as 'prod' by
 * default (this preserves the original behaviour where undefined === prod).
 *
 * No caching: every call re-reads the meta tag + URL so behaviour stays
 * deterministic across hot-reload and tests.
 */
export function getOttoEnv(): OttoEnv | undefined {
  const fromMeta = readMetaTag('otto-env');
  if (fromMeta !== undefined && VALID_ENVS.has(fromMeta)) {
    return fromMeta as OttoEnv;
  }
  if (typeof window === 'undefined') return undefined;
  const fromUrl = new URLSearchParams(window.location.search).get('env');
  // AFD's routing rule normalizes the env value via the Lowercase transform
  // before matching, so `?env=STAGING` routes to the staging origin. Mirror
  // that behaviour here so SPA detection lines up with AFD routing.
  const normalized = fromUrl?.toLowerCase();
  if (normalized && VALID_ENVS.has(normalized)) {
    return normalized as OttoEnv;
  }
  return undefined;
}

/**
 * Returns the current Otto deployment region (e.g., 'westus', 'eastasia')
 * read from the <meta name="otto-region"> tag injected by docker-entrypoint
 * from the ACA's OTTO_REGION env var.
 *
 * Returns undefined when not set (local dev, or ACA missing the env var).
 * Used by telemetryService to stamp cloud.roleInstance on every event so
 * a single Kusto query can pivot per region across all ACAs.
 */
export function getOttoRegion(): string | undefined {
  return readMetaTag('otto-region');
}

/**
 * Returns the App Insights connection string injected at container start
 * via the ACA's APPINSIGHTS_CONN env var, or undefined when the meta tag
 * wasn't substituted.
 *
 * Callers (telemetryService boot in main.tsx) should fall back to the
 * build-time-baked import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING
 * when this returns undefined — that keeps local pnpm dev + tests working
 * without requiring the meta tag.
 */
export function getAppInsightsConnectionString(): string | undefined {
  return readMetaTag('otto-appinsights-conn');
}
